// react_frontend/src/Components/Modules/PurchaseSetting/StockOut.js
import React, { useState, useEffect, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link, useNavigate } from "react-router-dom";
// RSuite Components
import {
  Button,
  Input,
  Badge,
  Loader,
  Message,
  toaster,
  Whisper,
  Tooltip,
  Checkbox,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
// Icons
import {
  FaBox,
  FaTags,
  FaArrowUp,
  FaSearch,
  FaClipboardList,
} from "react-icons/fa";

const StockOut = () => {
  const navigate = useNavigate();
  const { inventory, loadingInventory, startInventoryPolling, stopInventoryPolling } = useContext(MotherContext);
  const [formLoading, setFormLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [stockOutDate, setStockOutDate] = useState("");
  const [usedQuantities, setUsedQuantities] = useState({});
  const [stockOutAll, setStockOutAll] = useState(false);
  const isAdminOrAccount = ["1", "2"].includes(localStorage.getItem("who_am_i"));

  useEffect(() => {
    startInventoryPolling();
    return () => stopInventoryPolling();
  }, [startInventoryPolling, stopInventoryPolling]);

  // ✅ Preserve user input: only initialize new items
  useEffect(() => {
    setUsedQuantities((prev) => {
      const updated = { ...prev };
      inventory.forEach((item) => {
        if (item.id && !(item.id in updated)) {
          updated[item.id] = 0;
        }
      });
      return updated;
    });
  }, [inventory]);

  // Handle "Stock Out All" toggle
  useEffect(() => {
    if (inventory.length === 0) return;
    setUsedQuantities((prev) => {
      const updated = { ...prev };
      inventory.forEach((item) => {
        const available = (item.main_stock || 0) - (item.stock_out || 0);
        updated[item.id] = stockOutAll ? available : (updated[item.id] ?? 0);
      });
      return updated;
    });
  }, [stockOutAll, inventory]);

  const handleQuantityChange = (id, value) => {
    const numValue = Number(value) || 0;
    const item = inventory.find((d) => d.id === id);
    if (!item) return;
    const available = (item.main_stock || 0) - (item.stock_out || 0);
    if (numValue > available) {
      toaster.push(
        <Message type="warning" duration={3000}>
          Cannot use more than available: {available} {item.product_unit}
        </Message>,
        { placement: "topEnd" }
      );
      return;
    }
    setUsedQuantities((prev) => ({
      ...prev,
      [id]: numValue,
    }));
    if (numValue > 0 && stockOutAll) {
      setStockOutAll(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stockOutDate) {
      toaster.push(<Message type="error">Please select a date.</Message>);
      return;
    }

    setFormLoading(true);
    try {
      const payload = new FormData();
      payload.append("stock_out_date", stockOutDate);
      if (stockOutAll) {
        payload.append("stock_out_all", "1");
      } else {
        let hasQuantity = false;
        Object.entries(usedQuantities).forEach(([id, qty]) => {
          const numQty = Number(qty);
          if (numQty > 0) {
            hasQuantity = true;
            payload.append("id[]", id);
            payload.append("used_quantity[]", numQty);
          }
        });
        if (!hasQuantity) {
          toaster.push(<Message type="warning">Enter at least one quantity.</Message>);
          setFormLoading(false);
          return;
        }
      }

      const res = await AxiosAuthInstance.post("inventories", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        toaster.push(<Message type="success">Stock out recorded successfully!</Message>);
        navigate("/stock_list");
      } else {
        toaster.push(<Message type="error">{res.data.message || "Failed to record stock out."}</Message>);
      }
    } catch (err) {
      console.error("Submission error:", err);
      toaster.push(
        <Message type="error">
          {err.response?.data?.message || "An unexpected error occurred."}
        </Message>
      );
    } finally {
      setFormLoading(false);
    }
  };

  const filteredData = inventory.filter((item) =>
    (item.category?.category_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.item?.item_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.receipt_no || "").toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "100px",
      center: true,
    },
    {
      name: "Category",
      selector: (row) => row.category?.category_name,
      sortable: true,
      width: "250px",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaTags /> {row.category?.category_name || "N/A"}
        </Badge>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.item?.item_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaBox className="text-primary" />
          <span className="fw-medium">{row.item?.item_name || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "Available Stock",
      selector: (row) => (row.main_stock || 0) - (row.stock_out || 0),
      sortable: true,
      width: "200px",
      cell: (row) => {
        const available = (row.main_stock || 0) - (row.stock_out || 0);
        return (
          <Badge
            color={available > 0 ? "green" : "red"}
            content={`${available} ${row.product_unit}`}
            style={{ fontSize: "0.85em" }}
          />
        );
      },
    },
    {
      name: "Quantity to Stock Out",
      width: "200px",
      cell: (row) => {
        const available = (row.main_stock || 0) - (row.stock_out || 0);
        return isAdminOrAccount ? (
          <div className="d-flex align-items-center gap-2">
            <Input
              type="number"
              min={0}
              max={available}
              step={1}
              value={usedQuantities[row.id] ?? 0}
              onChange={(value) => handleQuantityChange(row.id, value)}
              disabled={stockOutAll}
              style={{
                width: 120,
                backgroundColor: stockOutAll ? "#f0f0f0" : "white",
              }}
            />
            <small className="text-muted">{row.product_unit}</small>
          </div>
        ) : (
          <Badge color="default">No Access</Badge>
        );
      },
    },
  ];

  const customStyles = {
    head: { style: { fontSize: "14px", fontWeight: "bold", backgroundColor: "#f8f9fa" } },
    rows: { style: { fontSize: "14px", minHeight: "60px" } },
    cells: { style: { padding: "12px 8px" } },
  };

  return (
    <>
      <Breadcrumb title="Stock Out" />
      <form onSubmit={handleSubmit}>
        <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <h5 className="text-dark mb-0">
            <FaArrowUp className="me-2" /> Record Stock Usage
          </h5>
          <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
            <Input
              placeholder="Search by item, category, or receipt..."
              value={search}
              onChange={setSearch}
              prefix={<FaSearch />}
              style={{ width: 300 }}
              size="md"
            />
            <Input
              type="date"
              value={stockOutDate}
              onChange={setStockOutDate}
              style={{ width: 160 }}
              size="md"
            />
            <Checkbox
              checked={stockOutAll}
              onChange={(value, checked) => setStockOutAll(checked)}
              style={{ fontSize: "14px" }}
            >
              Stock Out All
            </Checkbox>
            {isAdminOrAccount && (
              <Button
                className="custom-bg"
                appearance="primary"
                startIcon={<FaArrowUp />}
                type="submit"
                disabled={formLoading}
                style={{ minWidth: 160 }}
              >
                {formLoading ? (
                  <>
                    <Loader size="sm" /> Submitting...
                  </>
                ) : (
                  "Stock Out"
                )}
              </Button>
            )}
            <Whisper placement="top" speaker={<Tooltip>View current stock</Tooltip>}>
              <Link to="/stock_list" style={{ textDecoration: "none" }}>
                <Button appearance="subtle" startIcon={<FaClipboardList />} size="md">
                  Stock List
                </Button>
              </Link>
            </Whisper>
          </div>
        </div>
        <DataTable
          keyField="id"
          columns={columns}
          data={filteredData}
          pagination
          progressPending={loadingInventory}
          progressComponent={
            <div className="d-flex justify-content-center my-5">
              <Loader size="md" /> <span className="ms-2">Loading inventory...</span>
            </div>
          }
          noDataComponent={
            <div className="text-center my-5 text-muted">
              <FaBox size={48} className="mb-3 text-secondary" />
              <h5>No inventory found</h5>
              <p>Try adjusting your search or add new stock.</p>
            </div>
          }
          paginationPerPage={100}
          paginationRowsPerPageOptions={[100, 250, 500, 1000]}
          highlightOnHover
          responsive
          fixedHeader
          fixedHeaderScrollHeight="600px"
          customStyles={customStyles}
          striped
        />
      </form>
    </>
  );
};

export default StockOut;